<template>
   <div>
      <navbar-component />
      <main class="bg-blueGray-100">
         <!-- Hero: field problem → Frac Support answer -->
         <section
            class="relative overflow-hidden pt-16 py-20 md:py-28"
            :style="{ backgroundColor: '#111827' }"
         >
            <div
               class="absolute inset-0 bg-center bg-cover"
               :style="{ backgroundImage: `url(${heroImage})`, opacity: 0.45 }"
            ></div>
            <div
               class="absolute inset-0"
               :style="{ backgroundColor: 'rgba(0, 0, 0, 0.55)' }"
            ></div>
            <div class="container relative mx-auto px-4 md:px-6">
               <div class="max-w-3xl">
                  <span
                     class="inline-block bg-blue-600 text-white text-xs font-bold tracking-wider uppercase px-3 py-1 rounded-full mb-5 shadow-sm"
                  >
                     Field support
                  </span>
                  <h1
                     class="text-white font-bold text-4xl md:text-5xl lg:text-6xl leading-tight mb-5"
                  >
                     Frac Support
                  </h1>
                  <p
                     class="text-blueGray-100 text-lg md:text-xl leading-relaxed max-w-2xl mb-4"
                  >
                     When something on AMI controls or equipment goes wrong in
                     the field, crews need people who know the iron—not a ticket
                     queue.
                  </p>
                  <p
                     class="text-blueGray-300 text-base md:text-lg leading-relaxed max-w-2xl mb-8"
                  >
                     Frac Support gets you unstuck: upgrades, configuration,
                     troubleshooting, remote assistance, and training.
                  </p>
                  <button
                     type="button"
                     @click="scrollToContact"
                     class="inline-block bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200"
                  >
                     Contact Our Support Team
                     <i class="fas fa-arrow-right ml-2"></i>
                  </button>
               </div>
            </div>
         </section>

         <!-- Pain cards -->
         <section class="py-16 md:py-20 bg-blueGray-800">
            <div class="container mx-auto px-4 md:px-6">
               <div class="text-center max-w-4xl mx-auto mb-10 md:mb-12">
                  <h2 class="text-3xl md:text-4xl font-bold text-white mb-3">
                     Field problems we help solve
                  </h2>
                  <p class="text-lg text-blueGray-300 max-w-3xl mx-auto">
                     AMI Frac Support is aimed at the moments when treating,
                     setup, or the crew cannot wait on guesswork.
                  </p>
               </div>
               <div class="grid md:grid-cols-2 gap-5 md:gap-6 max-w-5xl mx-auto">
                  <div
                     v-for="item in painPoints"
                     :key="item.title"
                     class="bg-blueGray-900 rounded-xl p-6 border border-blueGray-700"
                  >
                     <div class="flex items-center gap-3 mb-3">
                        <div
                           class="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 border-2"
                           :class="item.iconWrapClass"
                        >
                           <i :class="item.icon"></i>
                        </div>
                        <h3 class="text-xl font-bold text-white leading-tight">
                           {{ item.title }}
                        </h3>
                     </div>
                     <p class="text-blueGray-400 text-sm mb-3">
                        <span class="font-semibold text-blueGray-300"
                           >The challenge:</span
                        >
                        {{ item.challenge }}
                     </p>
                     <p class="text-blueGray-200 text-sm md:text-base">
                        <span class="font-semibold text-lightBlue-300"
                           >How Frac Support helps:</span
                        >
                        {{ item.solution }}
                     </p>
                  </div>
               </div>
            </div>
         </section>

         <!-- Services + CTA -->
         <section class="py-16 md:py-20 bg-blueGray-900">
            <div class="container mx-auto px-4 md:px-6">
               <div class="text-center max-w-3xl mx-auto mb-10">
                  <h2 class="text-3xl md:text-4xl font-bold text-white mb-3">
                     What we help with
                  </h2>
                  <p class="text-blueGray-300 text-base md:text-lg">
                     Practical support across AMI controls, equipment, and the
                     people who run them. Operator manuals are available to
                     existing customers.
                  </p>
               </div>
               <div
                  class="flex flex-wrap justify-center gap-x-8 gap-y-8 max-w-6xl mx-auto mb-12"
               >
                  <div
                     v-for="item in supportItems"
                     :key="item.label"
                     class="flex flex-col items-center w-36 sm:w-40 px-2"
                  >
                     <i
                        :class="'fas ' + item.icon + ' fa-2x mb-3'"
                        :style="{ color: item.color }"
                     ></i>
                     <span
                        class="font-semibold text-white text-sm text-center leading-snug"
                        >{{ item.label }}</span
                     >
                  </div>
               </div>

               <div
                  class="bg-blueGray-800 rounded-2xl p-8 md:p-10 border border-blueGray-700 text-center max-w-3xl mx-auto"
               >
                  <h3 class="text-2xl font-bold text-white mb-4">
                     Need help on the pad?
                  </h3>
                  <p class="text-blueGray-300 mb-6">
                     Reach AMI Frac Support and we’ll help get your crew moving
                     again.
                  </p>
                  <button
                     type="button"
                     @click="scrollToContact"
                     class="inline-block bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200"
                  >
                     Contact Our Support Team
                     <i class="fas fa-arrow-right ml-2"></i>
                  </button>
               </div>
            </div>
         </section>
      </main>
      <footer-component />
   </div>
</template>

<script>
import NavbarComponent from "@/components/Navbars/IndexNavbar.vue";
import FooterComponent from "@/components/Footers/Footer.vue";
import { setPageSeo, serviceJsonLd, breadcrumbJsonLd } from "@/utils/seo";
import heroImage from "@/assets/img/industrial-engineer-programming-machine-control.jpg";

export default {
   name: "FracSupport",
   components: {
      NavbarComponent,
      FooterComponent,
   },
   data() {
      return {
         heroImage,
         painPoints: [
            {
               title: "Need help now",
               icon: "fas fa-life-ring",
               challenge:
                  "Something on the pad is wrong and treating cannot wait on tribal knowledge.",
               solution:
                  "Remote or on-call help from technicians who know AMI systems—not generic IT.",
               iconWrapClass: "bg-orange-900 text-orange-300 border-orange-400",
            },
            {
               title: "Config & upgrades",
               icon: "fas fa-cogs",
               challenge:
                  "New truck build, WaveEdge cutover, fleet/user/roles, or equipment mapping.",
               solution:
                  "Configuration and upgrade help so the system matches how the pad actually runs.",
               iconWrapClass: "bg-blue-900 text-lightBlue-300 border-lightBlue-400",
            },
            {
               title: "Parts & hardware",
               icon: "fas fa-tools",
               challenge:
                  "A dead panel, sensor, or controller stops treating.",
               solution:
                  "Hardware support and parts guidance to get the unit back online.",
               iconWrapClass: "bg-amber-900 text-amber-300 border-amber-400",
            },
            {
               title: "People & process",
               icon: "fas fa-chalkboard-teacher",
               challenge:
                  "New treaters / MPC seats, weak handoff, or unclear ownership of the system.",
                  solution:
                     "Training plus practical walkthroughs. Existing customers also get operator manuals and in-app FAQ when a reminder is enough.",
               iconWrapClass: "bg-emerald-900 text-emerald-300 border-emerald-500",
            },
         ],
         supportItems: [
            { label: "Upgrades", icon: "fa-arrow-up", color: "#10b981" },
            { label: "Configuration", icon: "fa-cogs", color: "#3b82f6" },
            {
               label: "Equipment Questions",
               icon: "fa-question-circle",
               color: "#f59e42",
            },
            { label: "Hardware Support", icon: "fa-tools", color: "#ef4444" },
            {
               label: "Troubleshooting",
               icon: "fa-life-ring",
               color: "#6366f1",
            },
            {
               label: "Remote Assistance",
               icon: "fa-headset",
               color: "#ec4899",
            },
            {
               label: "Training",
               icon: "fa-chalkboard-teacher",
               color: "#22c55e",
            },
            { label: "Documentation", icon: "fa-book", color: "#3b82f6" },
         ],
      };
   },
   mounted() {
      setPageSeo({
         title: "Frac Support | Field Help for AMI Controls | AMI",
         description:
            "Remote troubleshooting, configuration and upgrades, hardware and parts, plus training for treaters and MPC seats when the pad is stuck.",
         keywords:
            "frac support, hydraulic fracturing support, pump control support, remote assistance, WaveData support, WaveEdge support, AMI",
         path: "/fracsupport",
         jsonLd: [
            serviceJsonLd({
               name: "AMI Frac Support",
               description:
                  "Field support for AMI frac controls including remote troubleshooting, upgrades, parts, and training.",
               path: "/fracsupport",
            }),
            breadcrumbJsonLd([
               { name: "Home", path: "/" },
               { name: "Frac Support", path: "/fracsupport" },
            ]),
         ],
      });
   },
   methods: {
      scrollToContact() {
         this.$router.push({ path: "/" }).then(() => {
            this.$nextTick(() => {
               const contactSection = document.getElementById("contact");
               if (contactSection) {
                  contactSection.scrollIntoView({ behavior: "smooth" });
               }
            });
         });
      },
   },
};
</script>
